import { normalizePath } from 'Simple/Flow.js'
import React from 'react'
import pluralize from 'pluralize'
import makeDebug from 'Simple/debug.js'
import { DataProvider, useDataValue, useDataFormat } from 'Simple/Data.js'
import DefaultOption from './TysiaListDialogDefaultItemOption/view.js'
import Checkbox from './TysiaListDialogCheckbox/view.js'
import TysiaText from 'DesignSystem/TysiaText/index.js'
import { useDesignTokenValue } from 'Simple/DesignTokens.js'
import { groupBy, sortBy } from 'lodash'

let debug = makeDebug('simple/morph/view')

export default function Logic(props) {
  let viewPath = normalizePath(props.viewPath, '..').slice(0, -1)
  let item_key = useDataValue({
    context: 'list_dialog',
    viewPath: props.viewPath,
    path: 'item_key',
  })
  let group_by = useDataValue({
    context: 'list_dialog',
    viewPath: props.viewPath,
    path: 'group_by',
  })
  let list = useDataFormat({
    context: 'list_dialog',
    viewPath: props.viewPath,
    path: 'filtered_items',
    format: value =>
      group_by ? groupBy(sortBy(value, group_by), group_by) : value,
  })
  let isMultiChoice = useDataValue({
    context: 'list_dialog',
    viewPath: props.viewPath,
    path: 'isMultiChoice',
  })

  let dtSpaceLs = useDesignTokenValue({
    viewPath,
    path: 'space.ls',
  })
  let dtSpaceMs = useDesignTokenValue({
    viewPath,
    path: 'space.ms',
  })
  let dtSpaceXs = useDesignTokenValue({
    viewPath,
    path: 'space.xs',
  })
  let dtSpaceXxs = useDesignTokenValue({
    viewPath,
    path: 'space.xxs',
  })
  let dtSpaceXxxs = useDesignTokenValue({
    viewPath,
    path: 'space.xxxs',
  })
  let dtColorsNeutral500 = useDesignTokenValue({
    viewPath,
    path: 'colors.neutral.500',
  })

  let dataContextName =
    props.itemDataContextName ||
    defaultItemDataContextName('list_dialog_filtered_items')

  let childName = props?.childName || dataContextName
  return (
    <div
      style={{
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: '0%',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      {Array.isArray(list)
        ? renderItems(list, null, childName)
        : Object.entries(list).map(([group, options]) => (
            <div key={group}>
              <TysiaText
                viewPath={`${viewPath}/TysiaText`}
                weight="medium"
                color={dtColorsNeutral500}
                text={group}
                marginRight={dtSpaceXs}
                marginLeft={dtSpaceXs}
                marginTop={dtSpaceXxs}
                marginBottom={dtSpaceXxs}
              />
              {renderItems(options, group, childName)}
            </div>
          ))}
    </div>
  )

  function renderItems(list, group, childName) {
    return list.map((item, index) => {
      let id =
        item[item_key] ??
        item?.id ??
        item?._id ??
        `${index}${group ? `-${group}` : ''}`
      let childViewPath = `${viewPath}/TysiaListDialog/TysiaListDialogItemOption(${id})`

      return (
        <ListItem
          key={id}
          context={dataContextName}
          item={item}
          index={index}
          list={list}
          hasItemKey={false}
          parentViewPath={viewPath}
          viewPath={childViewPath}
        >
          <div
            style={{
              display: 'flex',
              flexGrow: 1,
              flexShrink: 1,
              flexBasis: '0%',
              flexDirection: 'row',
              alignItems: 'center',
              paddingTop: isMultiChoice ? dtSpaceMs : dtSpaceXxxs,
              paddingBottom: isMultiChoice ? dtSpaceMs : dtSpaceXxxs,
              paddingLeft: props.paddingLeft ?? dtSpaceLs,
              paddingRight: props.paddingRight ?? dtSpaceLs,
              gap: props.gap ?? dtSpaceLs,
            }}
          >
            {isMultiChoice && (
              <Checkbox viewPath={`${childViewPath}/Checkbox`} />
            )}
            {typeof props.children === 'undefined' ? (
              <DefaultOption
                {...{
                  index,
                  item,
                  indexReverse: list.length - index,
                  isFirst: index === 0,
                  isLast: index === list.length - 1,
                  viewPath: `${childViewPath}/DefaultOption`,
                  dsViewPath: `${childViewPath}/DefaultOption`,
                }}
              />
            ) : typeof props.children === 'function' ? (
              props.children({
                index,
                item,
                indexReverse: list.length - index,
                isFirst: index === 0,
                isLast: index === list.length - 1,
                viewPath: `${childViewPath}/${childName}`,
                dsViewPath: `${childViewPath}/${childName}`,
              })
            ) : (
              props.children
            )}
          </div>
        </ListItem>
      )
    })
  }
}

function ListItem(props) {
  let valueItem = React.useMemo(
    () => ({
      index: props.index,
      indexReverse: props.list.length - props.index,
      isFirst: props.index === 0,
      isLast: props.index === props.list.length - 1,
    }),
    [props.index, props.list]
  )

  if (
    process.env.NODE_ENV === 'development' &&
    !props.hasItemKey &&
    !props.item.id &&
    !props.item._id
  ) {
    debug({
      type: 'simple/data',
      warning: `Missing "id" or "_id" property on item in context "${props.context}". The list's index will be used as a fallback key. Consider setting "itemKey" if the item has a custom or compound key e.g. first_name,last_name.`,
      item: props.item,
      context: props.context,
      viewPath: props.viewPath,
    })
  }
  return (
    <DataProvider
      context={props.context}
      value={props.item}
      viewPath={props.viewPath}
    >
      <DataProvider
        context={`${props.context}_item`}
        value={valueItem}
        viewPath={props.viewPath}
      >
        {props.children}
      </DataProvider>
    </DataProvider>
  )
}

function defaultItemDataContextName(data) {
  if (!data) return ''
  return pluralize.singular(data)
}
